import { TagAssignment } from '@utils/types/organization/tag.ts';

/**
 * A tag assignment grouped with its siblings that share the same tag.
 * Used by the export "Tagged sections" summary.
 */
export interface GroupedTagAssignments {
  tagId: number;
  name: string;
  color?: string | null;
  count: number;
  assignments: TagAssignment[];
}

/**
 * Find the tag assignment whose character range fully contains [start, stop].
 *
 * Assignments carry `index_from`/`index_to` as strings (char offsets in the
 * rendered transcript). Entries with missing or non-numeric offsets are
 * ignored so a half-anchored assignment never matches. Returns the first
 * containing assignment, or undefined when none wraps the range.
 */
export const findAssignmentForRange = (
  assignments: TagAssignment[],
  start: number,
  stop: number
): TagAssignment | undefined => {
  for (const assignment of assignments) {
    const from =
      assignment.index_from != null ? Number(assignment.index_from) : NaN;
    const to = assignment.index_to != null ? Number(assignment.index_to) : NaN;
    if (!Number.isFinite(from) || !Number.isFinite(to)) {
      continue;
    }
    if (start >= from && stop <= to) {
      return assignment;
    }
  }
  return undefined;
};

/**
 * Group assignments by their tag, preserving document order within each group
 * and counting occurrences. Groups are sorted alphabetically by tag name so the
 * export summary renders deterministically.
 */
export const groupAssignmentsByTag = (
  assignments: TagAssignment[]
): GroupedTagAssignments[] => {
  const byTag = new Map<number, GroupedTagAssignments>();
  for (const assignment of assignments) {
    const existing = byTag.get(assignment.tag_id);
    if (existing) {
      existing.count += 1;
      existing.assignments.push(assignment);
    } else {
      byTag.set(assignment.tag_id, {
        tagId: assignment.tag_id,
        name: assignment.tag_name,
        color: assignment.tag_color,
        count: 1,
        assignments: [assignment]
      });
    }
  }
  return Array.from(byTag.values()).sort((a, b) =>
    a.name.localeCompare(b.name)
  );
};
